import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

interface NarrationTrackDraft {
  audio?: string;
  captions?: string;
  readalong?: string;
  narrator?: string;
  lang?: string;
  duration?: number;
  [key: string]: unknown;
}

function asRows(value: unknown): NarrationTrackDraft[] {
  return Array.isArray(value) ? (value as NarrationTrackDraft[]) : [];
}

/** `kind: "narrationTracks"` — `NarrationTrack[]` rows (`packages/core/core/
 *  manifest.ts`'s `NarrationTrackSchema`: `audio` required, everything else
 *  optional). An empty list collapses to `undefined`, not `[]`. */
export function NarrationTracksEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const rows = asRows(value);

  const commit = (next: NarrationTrackDraft[]) => onChange(next.length > 0 ? next : undefined);
  const update = (i: number, patch: Partial<NarrationTrackDraft>) =>
    commit(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => commit(rows.filter((_, idx) => idx !== i));
  const add = () => commit([...rows, { audio: "" }]);

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack spacing={1.5}>
        {rows.map((row, i) => (
          <Box key={i} sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <TextField
                size="small"
                label="Audio path"
                value={row.audio ?? ""}
                onChange={(e) => update(i, { audio: e.target.value })}
                sx={{ minWidth: 200, flex: 1 }}
              />
              <TextField
                size="small"
                label="Captions (WebVTT)"
                value={row.captions ?? ""}
                onChange={(e) => update(i, { captions: e.target.value || undefined })}
                sx={{ minWidth: 160 }}
              />
              <TextField
                size="small"
                label="Read-along sidecar"
                value={row.readalong ?? ""}
                onChange={(e) => update(i, { readalong: e.target.value || undefined })}
                sx={{ minWidth: 160 }}
              />
              <TextField
                size="small"
                label="Narrator"
                value={row.narrator ?? ""}
                onChange={(e) => update(i, { narrator: e.target.value || undefined })}
                sx={{ minWidth: 140 }}
              />
              <TextField
                size="small"
                label="Language"
                value={row.lang ?? ""}
                onChange={(e) => update(i, { lang: e.target.value || undefined })}
                sx={{ minWidth: 100 }}
              />
              <TextField
                size="small"
                type="number"
                label="Duration (s)"
                value={row.duration ?? ""}
                onChange={(e) => update(i, { duration: e.target.value === "" ? undefined : Number(e.target.value) })}
                sx={{ minWidth: 110 }}
              />
              <Button size="small" color="error" onClick={() => remove(i)}>
                Remove
              </Button>
            </Box>
          </Box>
        ))}
        <Button size="small" variant="outlined" onClick={add} sx={{ alignSelf: "flex-start" }}>
          + Add narration track
        </Button>
      </Stack>
    </Box>
  );
}
